import { memo, useCallback, useEffect, useRef, useState } from "react";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import type { DateTime } from "luxon";
import { Locale } from "../types";

type DatePickerComponentProps = {
  open: boolean;
  value: DateTime;
  onOpen: () => void;
  onClose: () => void;
  onAccept: (value: Date | null) => void;
  onClick: () => void;
  locale: Locale;
};

const DatePickerComponent = memo((props: DatePickerComponentProps) => {
  const { open, value, onOpen, onClose, onAccept, onClick, locale } = props;

  // Ref to the focusable input that acts as the trigger
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [forceFocus, setForceFocus] = useState(false);

  // Close wrapper that restores focus to the trigger
  const handleClose = useCallback(() => {
    onClose?.();
    // Defer to next frame so the popper unmount completes before focusing
    setTimeout(() => {
      const el = inputRef.current;
      if (el && document.body.contains(el)) {
        el.focus();
        setForceFocus(true);
      }
    }, 0);
  }, [onClose]);


  const handleAccept = useCallback(
    (date: Date | null) => {
      onAccept(date);
    },
    [onAccept]
  );

  useEffect(() => {
    const handleFocusChange = (e: FocusEvent) => {
      const input = inputRef.current;
      // ignore artificial focus events while closing
      if (!input) return;
      const newlyFocused = e.target as HTMLElement;
      const isInput = newlyFocused === input;
      const isInsidePopper =
        newlyFocused.closest('.MuiPickersPopper-root') !== null;
      // If focus is on input OR inside popper, keep highlight
      if (isInput || isInsidePopper) return;
      // Otherwise turn off highlight
      setForceFocus(false);
    };
    document.addEventListener('focusin', handleFocusChange);
    return () => {
      document.removeEventListener('focusin', handleFocusChange);
    };
  }, []);


  return (
    <DatePicker
      openTo="year"
      open={open}
      format={locale === Locale.roRO ? "dd.MM.yyyy" : "d MMM yyyy"}
      disableFuture={true}
      value={value.toJSDate()}
      onOpen={onOpen}
      onClose={handleClose}
      onAccept={handleAccept}
      slotProps={{
        popper: {
          disablePortal: true,   // KEY FIX — prevents MUI focus hijack
        },

        textField: {
          onClick,
          focused: forceFocus,
          inputRef,
          inputProps: {
            readOnly: true,
            // Prevent DatePicker from auto-reopening when we programmatically restore focus
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
              e.stopPropagation();
            },
          },
        },

      }}
    />
  );
});

export default DatePickerComponent;
